import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from './firebaseClient';
import { AppSettings } from '../types';

const SETTINGS_DOC_ID = 'global_settings';
const SETTINGS_COLLECTION = 'settings';

const DEFAULT_SETTINGS: AppSettings = {
  misCoordinatorLabel: 'MIS Coordinator',
  emailNotificationsEnabled: true,
  senderEmail: 'kreedodisha@gmail.com',
  emailTemplates: {
    applicationReceived: {
      subject: 'Confirmation of Registration in the Kreed Odisha Programme',
      title: 'Registration Confirmed!',
      message: 'Congratulations! Your registration in the Kreed Odisha Programme (KOP) has been successfully recorded. We warmly welcome you to our state-wide mission across all 30 districts of Odisha.\n\nYour Registration ID is {{id}}. Kindly preserve it for all future correspondence. Further details regarding the next steps will be shared with you in due course.\n\nWe look forward to your active engagement with the Programme.'
    },
    applicationVerified: {
      subject: 'Application Verified - Kreed Odisha',
      title: 'Verification Successful!',
      message: 'Congratulations! Your application has been verified and you have passed the first stage of the selection process. Please find your Training Confirmation Letter attached to this email.'
    },
    applicationRejected: {
      subject: 'Application Update - Kreed Odisha',
      title: 'Application Not Selected',
      message: 'Thank you for your interest in the Kreed Odisha program. After careful review, we regret to inform you that your application was not selected for the current cohort.'
    },
    selectionConfirmation: {
      subject: 'Training Selection Confirmation - Kreed Odisha',
      title: 'Selection Confirmed!',
      message: 'We are pleased to inform you that you have been selected for the upcoming training session of the Kreed Odisha Sports Excellence Program.'
    }
  }
};

let globalSettingsCache: { data: AppSettings; timestamp: number } | null = null;
const SETTINGS_CACHE_TTL_MS = 60 * 1000; // 60s cache avoids redundant document reads across pages

export const getGlobalSettings = async (forceRefresh: boolean = false): Promise<AppSettings> => {
  if (!forceRefresh && globalSettingsCache && (Date.now() - globalSettingsCache.timestamp < SETTINGS_CACHE_TTL_MS)) {
    return globalSettingsCache.data;
  }
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as AppSettings;
      globalSettingsCache = { data, timestamp: Date.now() };
      return data;
    } else {
      // Initialize with default settings if not exists
      await setDoc(docRef, DEFAULT_SETTINGS);
      globalSettingsCache = { data: DEFAULT_SETTINGS, timestamp: Date.now() };
      return DEFAULT_SETTINGS;
    }
  } catch (error) {
    console.error('Error fetching settings:', error);
    return globalSettingsCache ? globalSettingsCache.data : DEFAULT_SETTINGS;
  }
};

export const updateSettings = async (settings: Partial<AppSettings>): Promise<void> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    await setDoc(docRef, settings, { merge: true });
    if (globalSettingsCache) {
      globalSettingsCache.data = { ...globalSettingsCache.data, ...settings };
      globalSettingsCache.timestamp = Date.now();
    }
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

export const subscribeToSettings = (callback: (settings: AppSettings) => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data() as AppSettings);
    } else {
      callback(DEFAULT_SETTINGS);
    }
  });
};
